import * as Yup from 'yup';
import { get } from 'lodash';
import {FieldConfig, PropSource} from "@/modules/action-flow/config";
import {DataRenderConfig} from "@/modules/action-flow/config/types/dynamic-form-types";

export const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((prev, curr) => {
        return prev ? prev[curr] : undefined;
    }, obj);
};

export const setNestedValue = (obj: any, path: string, value: any) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const lastObj = keys.reduce((prev, curr) => {
        prev[curr] = prev[curr] || {};
        return prev[curr];
    }, obj);
    lastObj[lastKey!] = value;
};

export const resolveProps = (
    propSource: PropSource,
    context: any = {},
    parentData: any = {}
): Record<string, any> => {
    const resolvedProps: Record<string, any> = {};

    if (propSource.fromContext) {
        Object.entries(propSource.fromContext).forEach(([propName, contextPath]) => {
            resolvedProps[propName] = get(context, contextPath);
        });
    }

    if (propSource.fromParent) {
        Object.entries(propSource.fromParent).forEach(([propName, parentPath]) => {
            resolvedProps[propName] = get(parentData, parentPath);
        });
    }

    if (propSource.static) {
        Object.assign(resolvedProps, propSource.static);
    }

    return resolvedProps;
};

const createFieldSchema = (field: FieldConfig) => {
    let fieldSchema;

    switch (field.type) {
        case 'text':
            fieldSchema = Yup.string();
            if (field.validations?.min !== undefined) {
                fieldSchema = fieldSchema.min(field.validations.min);
            }
            if (field.validations?.max !== undefined) {
                fieldSchema = fieldSchema.max(field.validations.max);
            }
            if (field.validations?.pattern) {
                fieldSchema = fieldSchema.matches(new RegExp(field.validations.pattern));
            }
            break;
        case 'number':
            fieldSchema = Yup.number();
            if (field.validations?.min !== undefined) {
                fieldSchema = fieldSchema.min(field.validations.min);
            }
            if (field.validations?.max !== undefined) {
                fieldSchema = fieldSchema.max(field.validations.max);
            }
            break;
        case 'date':
            fieldSchema = Yup.date();
            break;
        case 'boolean':
            fieldSchema = Yup.boolean();
            break;
        case 'select':
        case 'multiselect':
            fieldSchema = Yup.string();
            break;
        default:
            fieldSchema = Yup.mixed();
    }

    if (field.validations?.required) {
        fieldSchema = fieldSchema.required(`${field.label} is required`);
    }

    return fieldSchema;
};

export const createValidationSchema = (config: DataRenderConfig) => {
    const buildSchemaRecursively = (fields: FieldConfig[]) => {
        const schemaFields: { [key: string]: any } = {};

        fields.forEach(field => {
            const fieldParts = field.key.split('.');
            let currentLevel = schemaFields;

            for (let i = 0; i < fieldParts.length; i++) {
                const part = fieldParts[i];
                if (i === fieldParts.length - 1) {
                    currentLevel[part] = createFieldSchema(field);
                } else {
                    if (!currentLevel[part]) {
                        currentLevel[part] = Yup.object();
                    }
                    currentLevel = currentLevel[part].fields || (currentLevel[part].fields = {});
                }
            }
        });

        return Yup.object().shape(schemaFields);
    };

    // @ts-ignore
    const allFields = config.steps.flatMap(step => step.fields || []);
    return buildSchemaRecursively(allFields);
};